import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Notice } from '../Toast'
import { Navigate, useNavigate } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { requestOtp, verifyOtp } from '../api/endpoints'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { EmailHelp, ErrorNotice, PublicShell } from '../components'

export default function LoginPage() {
  const { authed, login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const submitted = useRef('')

  const request = useMutation({ mutationFn: () => requestOtp(email.trim()), onSuccess: () => { setSent(true); setCooldown(30) } })
  const verify = useMutation({
    mutationFn: () => verifyOtp(email.trim(), code.trim()),
    onSuccess: (r) => {
      login(r.token)
      navigate('/', { replace: true })
    },
  })

  // The code is checked as soon as the sixth digit is in (typed or pasted); a failed code can be corrected without pressing anything.
  useEffect(() => {
    if (code.length === 6 && submitted.current !== code && !verify.isPending) {
      submitted.current = code
      verify.mutate()
    }
  }, [code, verify])
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const notRegistered = request.error instanceof ApiError && request.error.code === 'NOT_REGISTERED'

  if (authed) return <Navigate to="/" replace />

  const onEmail = (e: FormEvent) => {
    e.preventDefault()
    request.mutate()
  }
  const onCode = (e: FormEvent) => {
    e.preventDefault()
    verify.mutate()
  }
  // A wrong, expired or used code all look the same on purpose; say what to do, not why.
  const codeProblem = verify.error instanceof ApiError && verify.error.status === 401

  return (
    <PublicShell>
      <h1>Sign in</h1>
      {!sent ? (
        <form className="card" onSubmit={onEmail}>
          <p>Enter the email address your invitation was sent to. We will email you a 6-digit code. No password needed.</p>
          <label className="field">Email address
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {notRegistered
            ? <p role="alert" className="field-error">We could not find a registration for this email address. You can only sign in if you registered through your institution's registration link or received an invitation. Check the address you typed, or contact admissions.</p>
            : <ErrorNotice error={request.error} />}
          <button className="primary" type="submit" disabled={request.isPending}>{request.isPending ? 'Sending…' : 'Email me a code'}</button>
        </form>
      ) : (
        <form className="card" onSubmit={onCode}>
          <p role="status">If <strong>{email}</strong> is registered, a code is on its way. It expires in 10 minutes.</p>
          <label className="field">6-digit code
            <input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </label>
          {codeProblem
            ? <Notice tone="error">That code isn't right or has expired. Check it, or request a new one.</Notice>
            : <ErrorNotice error={verify.error} />}
          <button className="primary" type="submit" disabled={verify.isPending || code.length !== 6}>{verify.isPending ? 'Checking…' : 'Sign in'}</button>{' '}
          <p style={{ marginTop: 12 }}>
            <button type="button" className="link" disabled={cooldown > 0 || request.isPending} onClick={() => { setCode(''); submitted.current = ''; verify.reset(); request.mutate() }}>
              {cooldown > 0 ? `Send a new code (available in ${cooldown}s)` : 'Send a new code'}
            </button>{' · '}
            <button type="button" className="link" onClick={() => { setSent(false); setCode(''); submitted.current = ''; verify.reset() }}>Use a different email</button>
          </p>
          <EmailHelp />
        </form>
      )}
    </PublicShell>
  )
}
